import { ComponentStore } from '@ngrx/component-store';
import { concatLatestFrom } from '@ngrx/operators';
import { EMPTY, from, Observable, of, OperatorFunction } from 'rxjs';
import {
  bufferTime,
  catchError,
  debounceTime,
  filter,
  map,
  mergeMap,
  skip,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import { PaginatedStateLoadingMode, PaginatedStateModel } from './paginated-store.types';
import { PaginatedState } from './paginated.state';

/**
 * Abstract effects base class for paginated entity management.
 *
 * Responsibility: all async effects (list, count, delete, search, pagination).
 * Extends ComponentStore with empty state; delegates all state mutations to
 * the injected PaginatedState instance. Pagination/memory configuration is
 * read from the paired PaginatedState — Effects carries no config of its own
 * so the two sides cannot silently diverge.
 *
 * Subclasses must implement the four abstract repository methods.
 *
 * @template TData The data type for items (must have id property)
 * @template TState The full state shape of the paired PaginatedState (defaults to PaginatedStoreState&lt;TData&gt;)
 */
export abstract class PaginatedStateEffects<
  TData extends { id: string },
  TState extends PaginatedStateModel<TData> = PaginatedStateModel<TData>
> extends ComponentStore<Record<string, never>> {
  protected _suppressNextPageSizeReload = false;

  constructor(protected readonly paginatedState: PaginatedState<TData, TState>) {
    super({});

    // Assigned in the constructor (not field initializers): under ES2022 class-field
    // semantics, initializers run before constructor parameter properties are set.
    this.items$ = paginatedState.items$;
    this.loading$ = paginatedState.loading$;
    this.counting$ = paginatedState.counting$;
    this.searching$ = paginatedState.searching$;
    this.deleting$ = paginatedState.deleting$;
    this.error$ = paginatedState.error$;
    this.nextToken$ = paginatedState.nextToken$;
    this.hasMoreItems$ = paginatedState.hasMoreItems$;
    this.searchTerm$ = paginatedState.searchTerm$;
    this.totalCount$ = paginatedState.totalCount$;
    this.pageSize$ = paginatedState.pageSize$;
    this.previousTokens$ = paginatedState.previousTokens$;
    this.itemsLoaded$ = paginatedState.itemsLoaded$;
    this.noItems$ = paginatedState.noItems$;

    // Subscribe to search term changes (skip initial emission)
    this.searchItems(this.paginatedState.searchTerm$.pipe(skip(1)));
    // Subscribe to page size changes (skip initial emission)
    this.onPageSizeChange(this.paginatedState.pageSize$.pipe(skip(1)));
  }

  // ============================================================================
  // Selectors — re-exposed from PaginatedState
  // ============================================================================

  public readonly items$!: PaginatedState<TData, TState>['items$'];
  public readonly loading$!: PaginatedState<TData, TState>['loading$'];
  public readonly counting$!: PaginatedState<TData, TState>['counting$'];
  public readonly searching$!: PaginatedState<TData, TState>['searching$'];
  public readonly deleting$!: PaginatedState<TData, TState>['deleting$'];
  public readonly error$!: PaginatedState<TData, TState>['error$'];
  public readonly nextToken$!: PaginatedState<TData, TState>['nextToken$'];
  public readonly hasMoreItems$!: PaginatedState<TData, TState>['hasMoreItems$'];
  public readonly searchTerm$!: PaginatedState<TData, TState>['searchTerm$'];
  public readonly totalCount$!: PaginatedState<TData, TState>['totalCount$'];
  public readonly pageSize$!: PaginatedState<TData, TState>['pageSize$'];
  public readonly previousTokens$!: PaginatedState<TData, TState>['previousTokens$'];
  public readonly itemsLoaded$!: PaginatedState<TData, TState>['itemsLoaded$'];
  public readonly noItems$!: PaginatedState<TData, TState>['noItems$'];

  // ============================================================================
  // Public Effects
  // ============================================================================

  public readonly initializeItems = this.effect((trigger$: Observable<void>) =>
    trigger$.pipe(
      tap(() => {
        this.countItems();
        this.listItems({ searchTerm: undefined, lastItem: undefined });
      })
    )
  );

  public readonly initializeItemsAt = this.effect(
    (
      params$: Observable<{
        lastItem?: TData;
        pageSize?: number;
        previousTokens?: (TData | undefined)[];
      }>
    ) =>
      params$.pipe(
        concatLatestFrom(() => [this.paginatedState.pageSize$]),
        tap(([{ lastItem, pageSize, previousTokens }, currentPageSize]) => {
          const pageSizeChanged = pageSize != null && pageSize !== currentPageSize;

          if (pageSizeChanged) {
            this._suppressNextPageSizeReload = true;
          }
          if (pageSizeChanged || previousTokens) {
            this.paginatedState.seedPagination({
              pageSize: pageSizeChanged ? pageSize : undefined,
              previousPageTokens: previousTokens,
            });
          }

          this.countItems();
          this.listItems({ searchTerm: undefined, lastItem, limit: pageSize });
        })
      )
  );

  public readonly countItems = this.effect((trigger$: Observable<void>) =>
    trigger$.pipe(
      tap(() => this.paginatedState.setCounting(true)),
      concatLatestFrom(() => [this.paginatedState.searchTerm$]),
      switchMap(([, searchTerm]) =>
        from(this.countRepository(searchTerm)).pipe(
          tap((count) => this.paginatedState.setTotalCount(count)),
          this.carefulErrors()
        )
      )
    )
  );

  public readonly loadMoreItems = this.effect((action$: Observable<TData | undefined>) =>
    action$.pipe(
      tap(() => this.paginatedState.setLoading(true)),
      concatLatestFrom(() => [this.paginatedState.searchTerm$, this.paginatedState.hasMoreItems$]),
      tap(([lastItem, searchTerm, hasMoreItems]: [TData | undefined, string | undefined, boolean]) => {
        if (this.paginatedState.config.loadingMode === PaginatedStateLoadingMode.CONTINUOUS) {
          if (!hasMoreItems) {
            this.paginatedState.setLoading(false);
            return;
          }
          this.accumulateMoreItems(lastItem);
        } else {
          this.listItems({ searchTerm, lastItem });
        }
      })
    )
  );

  /**
   * Load *every* item matching the current search term in a single page, so a
   * "select all" acts on the full result set rather than only the rows already
   * scrolled into view. Uses the counted total as the page limit — consistent
   * with `relistPage`, which also lists with a limit larger than `pageSize`.
   */
  public readonly loadAllItems = this.effect((trigger$: Observable<void>) =>
    trigger$.pipe(
      tap(() => this.paginatedState.setLoading(true)),
      concatLatestFrom(() => [
        this.paginatedState.searchTerm$,
        this.paginatedState.totalCount$,
        this.paginatedState.pageSize$,
      ]),
      tap(([, searchTerm, totalCount, pageSize]) => {
        this.listItems({ searchTerm, lastItem: undefined, limit: Math.max(totalCount, pageSize) });
      })
    )
  );

  public readonly relistPage = this.effect((trigger$: Observable<void>) =>
    trigger$.pipe(
      tap(() => this.paginatedState.setLoading(true)),
      concatLatestFrom(() => [
        this.paginatedState.searchTerm$,
        this.paginatedState.previousTokens$,
        this.paginatedState.items$,
        this.paginatedState.pageSize$,
      ]),
      tap(([, searchTerm, previousTokens, items, pageSize]) => {
        if (this.paginatedState.config.loadingMode === PaginatedStateLoadingMode.CONTINUOUS) {
          const limit = items.length > 0 ? Math.ceil(items.length / pageSize) * pageSize : pageSize;
          this.listItems({ searchTerm, lastItem: undefined, limit });
        } else {
          const lastToken = previousTokens.length > 0 ? previousTokens[previousTokens.length - 1] : undefined;
          this.listItems({ searchTerm, lastItem: lastToken, limit: pageSize });
        }
      }),
      this.carefulErrors()
    )
  );

  public readonly deleteItem = this.effect((action$: Observable<string>) =>
    action$.pipe(
      tap(() => this.paginatedState.setDeleting(true)),
      switchMap((itemId) =>
        from(this.deleteRepositoryItem(itemId)).pipe(
          tap((success) => {
            if (success) {
              this.handleItemAfterDatasetRemoval(itemId);
            } else {
              this.paginatedState.setDeleting(false);
            }
          }),
          catchError((error) => {
            this.paginatedState.setError(error.message);
            return EMPTY;
          })
        )
      )
    )
  );

  // ============================================================================
  // Private Effects
  // ============================================================================

  protected readonly listItems = this.effect(
    (action$: Observable<{ searchTerm?: string; lastItem?: TData; limit?: number }>) =>
      action$.pipe(
        concatLatestFrom(() => [this.paginatedState.pageSize$]),
        switchMap(([{ searchTerm, lastItem, limit: requestedLimit }, pageSize]) =>
          this.consolidateFlow(this.listFlow(requestedLimit ?? pageSize, lastItem, searchTerm), lastItem)
        ),
        this.carefulErrors()
      )
  );

  private readonly accumulateMoreItems = this.effect((action$: Observable<TData | undefined>) =>
    action$.pipe(
      concatLatestFrom(() => [this.paginatedState.hasMoreItems$]),
      filter(([, hasMoreItems]) => hasMoreItems),
      concatLatestFrom(() => [this.paginatedState.searchTerm$, this.paginatedState.nextToken$]),
      tap(([[lastItem], searchTerm, nextToken]) => {
        this.listItems({ searchTerm, lastItem: lastItem ?? nextToken });
      })
    )
  );

  private readonly searchItems = this.effect((searchTerm$: Observable<string | undefined>) =>
    searchTerm$.pipe(
      debounceTime(300),
      switchMap((searchTerm: string | undefined) => {
        this.countItems();
        this.listItems({ searchTerm, lastItem: undefined });
        return EMPTY;
      })
    )
  );

  protected readonly onPageSizeChange = this.effect((action$: Observable<number>) =>
    action$.pipe(
      filter(() => {
        if (this._suppressNextPageSizeReload) {
          this._suppressNextPageSizeReload = false;
          return false;
        }
        return true;
      }),
      concatLatestFrom(() => [this.paginatedState.searchTerm$]),
      tap(([pageSize, searchTerm]) => this.listItems({ searchTerm, lastItem: undefined, limit: pageSize }))
    )
  );

  // ============================================================================
  // Protected Helper Methods
  // ============================================================================

  protected carefulErrors<T>(): OperatorFunction<T, T> {
    return catchError((error) => {
      console.error(error);
      this.paginatedState.setError(error.message);
      return EMPTY;
    });
  }

  protected countAttribute(countOperation: Promise<number>, itemId: string, defaultValue = 0): Observable<number> {
    return from(countOperation).pipe(
      catchError((error) => {
        console.error(`Error loading counts for ${itemId}:`, error);
        return of(defaultValue);
      })
    );
  }

  protected handleItemAfterDatasetRemoval(itemId: string): void {
    this.countItems();

    if (this.paginatedState.config.loadingMode === PaginatedStateLoadingMode.PAGINATED) {
      this.paginatedState.previousTokens$.pipe(take(1)).subscribe((previousTokens) => {
        const lastToken = previousTokens.length > 0 ? previousTokens[previousTokens.length - 1] : undefined;
        this.loadMoreItems(lastToken);
      });
    } else {
      this.paginatedState.removeItem(itemId);
    }
  }

  protected consolidateFlow(items$: Observable<TData[] | undefined>, lastItem: TData | undefined): Observable<TData[]> {
    return items$.pipe(
      filter((items): items is TData[] => items !== undefined),
      tap((items: TData[]) => this.consolidateItems(items, lastItem)),
      this.carefulErrors()
    );
  }

  private consolidateItems(items: TData[], lastItem: TData | undefined): void {
    this.paginatedState.updateItems({ items, lastItem });

    from(items)
      .pipe(
        mergeMap(
          (item) =>
            this.retrieveDeferredAttributes(item.id).pipe(
              switchMap((deferredAttributes) => {
                if (deferredAttributes) {
                  this.paginatedState.updateItemById({ id: item.id, changes: deferredAttributes });
                  return of(undefined);
                }
                return this.processDeferredAttributes(item.id).pipe(
                  bufferTime(200),
                  tap((changesBatch) => {
                    if (changesBatch.length > 0) {
                      const mergedChanges = Object.assign({}, ...changesBatch);
                      this.paginatedState.updateItemById({ id: item.id, changes: mergedChanges });
                      if (this.paginatedState.config.useMemory) {
                        this.paginatedState.storeDeferredAttributes({ itemId: item.id, changes: mergedChanges });
                      }
                    }
                  })
                );
              })
            ),
          this.paginatedState.config.maxConcurrentCountQueries
        )
      )
      .subscribe();
  }

  /**
   * Point-in-time snapshot of one item's remembered deferred attributes.
   * Deliberately a `take(1)` read — a live subscription here would couple the
   * read to the concurrent `storeDeferredAttributes` writes in `consolidateItems`.
   */
  private retrieveDeferredAttributes(itemId: string): Observable<Partial<TData> | undefined> {
    if (!this.paginatedState.config.useMemory) {
      return of(undefined);
    }
    return this.paginatedState.deferredAttributeMemory$.pipe(
      take(1),
      map((memory) => memory[itemId])
    );
  }

  // ============================================================================
  // Abstract Methods (subclasses must implement)
  // ============================================================================

  protected abstract countRepository(searchTerm?: string): Promise<number>;

  protected abstract listFlow(
    limit: number,
    lastItem: TData | undefined,
    searchTerm: string | undefined
  ): Observable<TData[]>;

  protected abstract deleteRepositoryItem(itemId: string): Promise<boolean>;

  protected abstract processDeferredAttributes(itemId: string): Observable<Partial<TData>>;
}
